import React, { useMemo } from "react";
import { spring, interpolate } from "remotion";
import { SvgIcon } from "../../components/SvgComponents";

interface IntroProps {
  headline: string;
  keywords: string[];
  introIcon?: string;
  frame: number;
  fps: number;
}

const Character: React.FC<{
  char: string;
  index: number;
  frame: number;
  fps: number;
}> = ({ char, index, frame, fps }) => {
  const delay = index * 2; // 2 frames stagger between characters
  const spr = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 12,
      mass: 0.5,
      stiffness: 100,
    },
  });

  const opacity = interpolate(frame - delay, [0, 5], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const scale = interpolate(spr, [0, 1], [0.5, 1]);
  const translateY = interpolate(spr, [0, 1], [20, 0]);

  return (
    <span
      style={{
        display: "inline-block",
        whiteSpace: char === " " ? "pre" : "normal",
        opacity,
        transform: `scale(${scale}) translateY(${translateY}px)`,
      }}
    >
      {char}
    </span>
  );
};

export const GeometricLiquidIntro: React.FC<IntroProps> = ({
  headline,
  introIcon,
  frame,
  fps,
}) => {
  const chars = useMemo(() => headline.split(""), [headline]);

  const svgScale = spring({
    frame: frame - 15, // Start after text starts appearing
    fps,
    config: { damping: 12, mass: 0.8, stiffness: 120 },
  });

  const svgOpacity = interpolate(frame - 15, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const floatY = Math.sin(frame * 0.05) * 15;

  return (
    <div
      style={{
        width: "100%",
        padding: "40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "40px",
      }}
    >
      {/* Decorative Line Tag */}
      <div
        style={{
          padding: "8px 20px",
          borderRadius: "4px",
          background: "#bef264", // Electric Lime
          color: "#0f172a",
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 700,
          fontSize: "14px",
          letterSpacing: "3px",
          textTransform: "uppercase",
          boxShadow: "0 0 20px rgba(190, 242, 100, 0.3)",
        }}
      >
        Breaking Data
      </div>

      {/* Staggered Headline */}
      <h1
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "56px",
          fontWeight: 700,
          lineHeight: 1.1,
          color: "#ffffff",
          margin: 0,
          perspective: "1000px",
        }}
      >
        {chars.map((char, i) => (
          <Character key={i} char={char} index={i} frame={frame} fps={fps} />
        ))}
      </h1>

      {/* Hero SVG with floating animation */}
      <div
        style={{
          width: "100%",
          height: "320px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          opacity: svgOpacity,
          transform: `scale(${svgScale}) translateY(${floatY}px)`,
        }}
      >
        <div
          style={{
            filter: "drop-shadow(0 0 30px rgba(34, 211, 238, 0.4))",
          }}
        >
          <SvgIcon
            name={introIcon || "undraw-pcsocial-16rw"}
            width={320}
            height={320}
            primaryColor="#22d3ee" // Neon Cyan
          />
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
